import React from 'react'
import Modal from '../../../components/Modal'
import CustomSelect from '../../../components/CustomSelect/CustomSelect'

export const PROVIDERS = {
  ivideon: 'Ivideon',
  rostelecom: 'Ростелеком',
  trassir: 'TRASSIR',
  line: 'Линия',
  other: 'Другой провайдер'
}

/**
 * Модальное окно: Добавление / Редактирование камеры
 */
export function CameraManageModal({ isOpen, onClose, camera, formData, setFormData, onSave, saving }) {
  const isIvideonCamera = camera?.ivideon_server_id

  return (
    <Modal 
      isOpen={isOpen}
      title={camera ? 'Настройки камеры' : 'Добавить новую камеру'} 
      onClose={onClose}
    >
      <div className="camera-form-content">
        {/* Если Ivideon-камера — показываем инфо */}
        {camera?.ivideon_server_id && (
          <div style={{
            padding: '10px 14px', marginBottom: '16px',
            background: 'rgba(124, 58, 237, 0.06)',
            border: '1px solid rgba(124, 58, 237, 0.15)',
            borderRadius: 'var(--border-radius-md)',
            fontSize: '12px', color: 'var(--text-secondary)',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <span style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg></span>
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Камера Ivideon</div>
              <div>
                Сервер: {camera.ivideon_server_name || '—'}
                {camera.resolution && ` • ${camera.resolution}`}
              </div>
            </div>
          </div>
        )}

        <div className="input-group">
          <label>Название камеры</label>
          <input 
            type="text" 
            className="input"
            placeholder="Например: Зона выдачи, Склад, Вход"
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
          />
        </div>

        {/* Провайдер и embed_url — только для НЕ-Ivideon камер */}
        {!isIvideonCamera && (
          <>
            <div className="input-group">
              <CustomSelect
                label="Облачной провайдер"
                value={formData.provider}
                onChange={val => setFormData({ ...formData, provider: val })}
                options={Object.entries(PROVIDERS).map(([key, label]) => ({
                  value: key,
                  label: label,
                }))}
              />
            </div>

            <div className="input-group">
              <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span>Embed-ссылка на видеопоток</span>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 400 }}>
                  Ссылка для iframe (например: https://open.ivideon.com/embed/v2/...)
                </span>
              </label>
              <input 
                type="url" 
                className="input"
                placeholder="https://..."
                value={formData.embed_url}
                onChange={e => setFormData({ ...formData, embed_url: e.target.value })}
              />
            </div>
          </>
        )}

        <div className="form-checkbox-group" style={{ marginTop: '8px' }}>
          <label className="checkbox-container" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
            <input 
              type="checkbox"
              checked={formData.restrict_access}
              onChange={e => setFormData({ ...formData, restrict_access: e.target.checked })}
              style={{ marginTop: '2px' }}
            />
            <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
              Ограничить доступ к камере
              <div style={{ fontSize: '11.5px', color: 'var(--text-secondary)', marginTop: '4px', lineHeight: '1.4', fontWeight: 400 }}>
                Камера будет скрыта от всех сотрудников, кроме явно добавленных в белый список.
              </div>
            </span>
          </label>
        </div>

        {camera && (
          <div className="form-checkbox-group" style={{ marginTop: '8px' }}>
            <label className="checkbox-container" style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
              <input 
                type="checkbox"
                checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>Активная камера (включена)</span>
            </label>
          </div>
        )}

        <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
          <button 
            className="btn btn-secondary" 
            onClick={onClose}
            disabled={saving}
          >
            Отмена
          </button>
          <button 
            className="btn btn-primary" 
            onClick={onSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : camera ? 'Сохранить изменения' : 'Добавить камеру'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Модальное окно: Доступы сотрудников к камере
 */
export function CameraAccessModal({ isOpen, onClose, camera, employees, grantedUserIds, onToggleAccess, savingAccess }) {
  return (
    <Modal 
      isOpen={isOpen}
      title={`Права доступа: ${camera?.name || 'камера'}`} 
      onClose={onClose}
    >
      <div className="camera-access-content">
        <p className="access-info-text">
          Выберите сотрудников, которые смогут просматривать трансляцию с этой камеры. Владельцы и администраторы имеют доступ по умолчанию.
        </p>

        <div className="employees-access-list">
          {employees.length === 0 ? (
            <div className="empty-employees-placeholder">Нет доступных сотрудников</div>
          ) : (
            employees.map(emp => {
              const isChecked = grantedUserIds.includes(emp.id)
              const isEmpAdmin = ['owner', 'admin', 'admin_employee'].includes(emp.role)
              
              return (
                <div 
                  key={emp.id} 
                  className={`employee-access-row ${isEmpAdmin ? 'admin-row' : ''}`}
                >
                  <div className="employee-info">
                    <span className="employee-name">{emp.full_name}</span>
                    <span className="employee-role">
                      {emp.role === 'admin' || emp.role === 'admin_employee' ? 'Администратор' : emp.role === 'helper' ? 'Помощник' : 'Сотрудник'}
                    </span>
                  </div>
                  
                  {isEmpAdmin ? (
                    <span className="always-access-badge">Всегда доступно</span>
                  ) : (
                    <label className="switch">
                      <input 
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggleAccess(emp.id)}
                        disabled={savingAccess}
                      />
                      <span className="slider round" />
                    </label>
                  )}
                </div>
              )
            })
          )}
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" onClick={onClose}>
            Готово
          </button>
        </div>
      </div>
    </Modal>
  )
}
